import { ReactNode, useEffect, useRef, useState } from 'react';

// material-ui
import { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography, { TypographyProps } from '@mui/material/Typography';

// ==============================|| TRUNCATED TEXT (tooltip on overflow) ||============================== //

interface TruncatedTextProps extends Omit<TypographyProps, 'title'> {
  children: ReactNode;
  lines?: number; // 1 = single-line ellipsis (default); >1 = multi-line clamp
  title?: string; // explicit tooltip text (defaults to children when it's a string)
}

export default function TruncatedText({ children, lines = 1, title, sx, ...rest }: TruncatedTextProps) {
  const ref = useRef<HTMLElement>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      setTruncated(lines > 1 ? el.scrollHeight > el.clientHeight + 1 : el.scrollWidth > el.clientWidth + 1);
    };
    check();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(check);
      ro.observe(el);
    }
    return () => ro?.disconnect();
  }, [children, lines, title]);

  const clampSx =
    lines > 1
      ? {
          display: '-webkit-box',
          WebkitLineClamp: lines,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const };

  const mergedSx = [clampSx, ...(Array.isArray(sx) ? sx : [sx])].filter(Boolean) as SxProps<Theme>;
  const tip = title ?? (typeof children === 'string' ? children : '');

  return (
    <Tooltip title={truncated ? tip : ''} disableInteractive>
      {/* data-self-tooltip: tooltip-ul generic de celulă (CellOverflowTooltip) nu se dublează. */}
      <Typography ref={ref} data-self-tooltip="" sx={mergedSx} {...rest}>
        {children}
      </Typography>
    </Tooltip>
  );
}
